import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSalon } from "../lib/salon";
import { formatTime, naiveISO, isSameDay, isoOrder } from "../lib/time";
import { CalendarBlank, ListBullets, UsersThree, CurrencyDollar, Plus, ArrowUpRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import BookingModal from "../components/BookingModal";
import { useRefresh } from "../context/RefreshContext";

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

const fmtTime = formatTime;

function StatCard({ label, value, icon: Icon, sub, to, testId, subTo }) {
  const inner = (
    <div className="surface-card surface-card-hover p-6 flex flex-col h-full" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center">
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="mt-5 font-heading text-4xl tracking-tight font-light">{value}</div>
      {sub != null && (
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
          {sub}
          {to && <ArrowUpRight size={11} className="opacity-60" />}
        </div>
      )}
    </div>
  );
  if (subTo && sub) {
    // Clickable card with a separate sub-link to a different filter
    return (
      <div className="relative h-full">
        <Link to={to} data-testid={`${testId}-link`} className="block h-full">
          {inner}
        </Link>
        <Link
          to={subTo}
          data-testid={`${testId}-sublink`}
          className="absolute bottom-5 right-5 text-[11px] underline underline-offset-2 text-muted-foreground hover:text-foreground"
        >
          view
        </Link>
      </div>
    );
  }
  if (to) return <Link to={to} data-testid={`${testId}-link`} className="block h-full">{inner}</Link>;
  return inner;
}

export default function Dashboard() {
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const { bookingsVersion } = useRefresh();
  const salon = useSalon();
  const currency = salon?.currency_symbol || "$";

  const fetchAll = async () => {
    const today = startOfDay(new Date());
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);
    // Range params use NAIVE salon-local ISO so they match the wire format
    // used by the backend (no timezone shift).
    const [s, sv, c, todayB, weekB] = await Promise.all([
      api.get("/stylists").then((r) => r.data),
      api.get("/services").then((r) => r.data),
      api.get("/customers").then((r) => r.data),
      api.get("/bookings", { params: { start: naiveISO(today), end: naiveISO(tomorrow) } }).then((r) => r.data),
      api.get("/bookings", { params: { start: naiveISO(tomorrow), end: naiveISO(weekEnd) } }).then((r) => r.data),
    ]);
    setStylists(s); setServices(sv); setCustomers(c);
    setTodayBookings(todayB); setUpcomingBookings(weekB);
  };

  useEffect(() => { fetchAll(); }, [bookingsVersion]);

  const todayScheduled = useMemo(
    () => todayBookings.filter((b) => b.status === "scheduled"),
    [todayBookings]
  );
  const todayCompleted = useMemo(
    () => todayBookings.filter((b) => b.status === "completed"),
    [todayBookings]
  );
  const todayCancelled = useMemo(
    () => todayBookings.filter((b) => b.status === "cancelled"),
    [todayBookings]
  );
  const todayVisible = useMemo(
    () => todayBookings.filter((b) => b.status !== "cancelled"),
    [todayBookings]
  );
  const todayRevenue = useMemo(
    () => todayVisible.reduce((sum, b) => sum + (b.price || 0), 0),
    [todayVisible]
  );
  const upcomingScheduled = useMemo(
    () => upcomingBookings.filter((b) => b.status === "scheduled").length,
    [upcomingBookings]
  );

  return (
    <div className="space-y-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Today · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight font-light mt-2">
            Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}.
          </h1>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-primary hover:bg-[#2B4F39]"
          data-testid="dashboard-new-booking-btn"
        >
          <Plus size={16} className="mr-1" /> New booking
        </Button>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="kpi-grid">
        <StatCard
          label="Today's bookings"
          value={todayScheduled.length}
          icon={CalendarBlank}
          sub={
            todayCancelled.length > 0
              ? `${todayCompleted.length} done · ${todayCancelled.length} cancelled`
              : todayCompleted.length > 0
              ? `${todayCompleted.length} done`
              : "Open today's schedule"
          }
          to="/calendar"
          subTo={todayCancelled.length > 0 ? "/bookings?filter=cancelled" : undefined}
          testId="kpi-today"
        />
        <StatCard
          label="Upcoming (7 days)"
          value={upcomingScheduled}
          icon={ListBullets}
          sub="Open scheduled bookings"
          to="/bookings?filter=active"
          testId="kpi-upcoming"
        />
        <StatCard
          label="Customers"
          value={customers.length}
          icon={UsersThree}
          sub="Open customer list"
          to="/customers"
          testId="kpi-customers"
        />
        <StatCard
          label="Today's revenue"
          value={`${currency}${todayRevenue.toFixed(0)}`}
          icon={CurrencyDollar}
          sub="Estimated · scheduled + completed"
          testId="kpi-revenue"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="surface-card p-6 lg:col-span-2" data-testid="today-schedule">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-2xl tracking-tight">Today's schedule</h2>
            <Link to="/calendar" className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground" data-testid="today-schedule-link">
              Open calendar →
            </Link>
          </div>
          {todayVisible.length === 0 ? (
            <div className="text-sm text-muted-foreground py-12 text-center">
              No bookings today. Book the first one above.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {todayVisible
                .slice()
                .sort((a, b) => isoOrder(a.start_at) - isoOrder(b.start_at))
                .map((b) => (
                  <li key={b.id} className="py-4 flex items-center gap-4" data-testid={`today-booking-${b.id}`}>
                    <div className="w-16 text-sm font-mono">{fmtTime(b.start_at)}</div>
                    <div
                      className="w-1 h-10 rounded-full"
                      style={{ background: b.stylist?.color || "#1A3626" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium truncate ${b.status === "completed" ? "text-muted-foreground" : ""}`}>
                        {b.customer?.name || "—"}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {b.service?.name} · {b.stylist?.name}
                      </div>
                    </div>
                    {b.status === "completed" && (
                      <span className="text-[10px] px-2 py-1 rounded-md bg-[#EFEBE3] text-foreground">done</span>
                    )}
                    <div className="text-sm font-mono">{currency}{b.price?.toFixed?.(0) ?? 0}</div>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="surface-card p-6" data-testid="stylist-list">
          <h2 className="font-heading text-2xl tracking-tight mb-5">Stylists</h2>
          <ul className="space-y-3">
            {stylists.map((s) => {
              const todayCount = todayScheduled.filter((b) => b.stylist_id === s.id).length;
              return (
                <li key={s.id} className="flex items-center gap-3">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ background: s.color || "#1A3626" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.active ? "Active" : "Off"}</div>
                  </div>
                  <div className="text-xs font-mono">{todayCount} today</div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <BookingModal
        open={open}
        onOpenChange={setOpen}
        onCreated={fetchAll}
        stylists={stylists}
        services={services}
      />
    </div>
  );
}
